import React from "react";
import { refreshEstimateDatasource } from "../../excel/datasources/estimate";
import { refreshContractingDatasource } from "../../excel/datasources/bidTabs";
import { clearEstimateSelection } from "../../excel/lifecycle";
import { clearGCGRCaches, refreshGCGRCaches } from "../../api/gcgrs";
import { clearContractingCaches } from "../../api/bids";
import { clearCostModelCaches, refreshCostModelCaches } from "../../api/costModels";
import { fetchEstimatesByProjectId } from "../../api";
import { clearAllChartData } from "../../excel/charts/api/registry";
import { useLoading } from "../context/LoadingContext";
import { useProject } from "../context/ProjectContext";
import { EdiphiDatasourceType, useDatasources } from "../context/DatasourceContext";
import { useDialog } from "../context/DialogContext";
import { DialogDescription } from "../shared/dialog/Dialog";
import { useUserSettings } from "../context/UserSettingsContext";
import { useLoaderDialog } from "./useLoaderDialog";
import { costModelLabel } from "../../utils/format";
import { setCalculationAutomatic, setCalculationManual } from "../../excel/workbook";
import { refreshAllCharts } from "../../excel/charts/api/registry";
import { discoverExistingCharts } from "../../excel/charts/api/registry";
import { ReportTemplate } from "../../excel/reports/api";
import { clearReportWorksheets, discoverReportTemplates, refreshAllReports } from "../../excel/reports/api/registry";
import { reportTypeOfTemplate } from "./useReportCards";
import { worksheetCache } from "../../excel/workbook/cache";
import { getLoadedEstimateId } from "../../excel/workbook/state";
import { LinearLoaderError } from "../shared/loaders/LinearLoader";
import { DatasourceGoneError, ForbiddenError, handleError } from "../../errors";
import Logger from "../../clients/sentry";
import { hasSession } from "../../utils/session";
import { EstimateData } from "../../interfaces/EstimateData";
import { Estimate } from "../../interfaces/Estimate";
import { GCGR } from "../../interfaces/GCGRs";
import { CostModel } from "../../interfaces/CostModel";
import {
  CHARTS_LOAD_FAILED_TITLE,
  CONTRACTING_PICK_GONE_MESSAGE,
  COST_MODEL_PICK_GONE_MESSAGE,
  COST_MODEL_UNAVAILABLE_TITLE,
  DATASOURCE_LOAD_FAILED_TITLE,
  DATASOURCE_NOT_FOUND_TITLE,
  DATASOURCE_REMOVED_SHEETS_EMPTIED,
  ESTIMATE_PICK_GONE_MESSAGE,
  GCGR_PICK_GONE_MESSAGE,
  KEEP_ACTION,
  LOADER_CHARTS_ERROR,
  LOADER_DATASOURCES_ERROR,
  LOADER_REPORTS_ERROR,
  LOADER_TABLES_ERROR,
  LOADING_CHARTS_TITLE,
  LOADING_CONTRACTING_TITLE,
  LOADING_COST_MODEL_TITLE,
  LOADING_DATASOURCE_TITLE,
  LOADING_ESTIMATE_TITLE,
  LOADING_GCGR_TITLE,
  LOADING_REPORTS_TITLE,
  LOADING_TABLES_TITLE,
  REMOVE_ACTION,
  REMOVE_ITS_CARD_PROMPT,
  REPORTS_LOAD_FAILED_TITLE,
  TABLES_LOAD_FAILED_TITLE,
  datasourceGoneDescription,
} from "../constants";
import {
  clearContractingReportTable,
  clearCostModelReportTable,
  clearGCGRReportTable,
  tabulateContractingReportData,
  tabulateCostModelReportData,
  tabulateEstimateReportData,
  tabulateGCGRReportData,
  tabulateProjectReportData,
} from "../../excel/datasources/reportTables";
import {
  clearContractingDataTables,
  clearEstimateDataTables,
  clearGcgrDataTables,
  loadContractingData,
  loadEstimateData,
  loadGCGRData,
} from "../../excel/reports/dataTables";

/**
 * What a load actually achieved. `dataLoaded` is the one the drawers care about: it says every target's
 * own data reached the workbook, so a card is earned even if a later phase failed. A target that turned
 * out not to exist leaves it false — nothing landed, so nothing should be registered.
 */
export type LoadOutcome = {
  dataLoaded: boolean;
  completed: boolean;
};

/** A target the pass could not complete, collected so it can ask about it once it has finished. */
type MissingTarget = {
  type: EdiphiDatasourceType;
  label: string;
  /**
   * Replaces the not-found wording for a target that does exist but cannot be used — a cost model
   * the user has no permission to read is still there, so saying it is gone would be wrong.
   */
  notice?: { title: string; description: string };
};

export type LoadTarget =
  | { type: "estimate"; estimate: Estimate }
  | { type: "gcgr"; gcgr: GCGR }
  | { type: "contracting" }
  | { type: "cost-model"; costModel: CostModel };

/**
 * Module-scoped so the drawer's loader instances and the container's serialise against each other.
 * A per-hook ref only guards the component that owns it, which let a second click start a
 * concurrent pass over the same workbook.
 */
let isLoading = false;

const PHASE_ONE_TITLE: Record<LoadTarget["type"], string> = {
  estimate: LOADING_ESTIMATE_TITLE,
  gcgr: LOADING_GCGR_TITLE,
  contracting: LOADING_CONTRACTING_TITLE,
  "cost-model": LOADING_COST_MODEL_TITLE,
};

const phaseOneTitle = (targets: LoadTarget[], namePerDatasource: boolean): string =>
  namePerDatasource && targets.length === 1 ? PHASE_ONE_TITLE[targets[0].type] : LOADING_DATASOURCE_TITLE;

export function useDatasourceLoader() {
  const {
    selectedProject,
    selectedEstimate,
    selectedGcgr,
    isContractingLoaded,
    selectedCostModel,
    setSelectedEstimate,
    setSelectedGcgr,
    setIsContractingLoaded,
    setSelectedCostModel,
  } = useProject();
  const { markDataRefreshed, removeDatasource } = useDatasources();
  const { withLoading, withReportLoading, withChartLoading } = useLoading();
  const { settings } = useUserSettings();
  const { showDialog, hideDialog } = useDialog();
  const { setSectionCount, updateDialog, showSuccessAndAutoClose, clearTimeoutRef, reset } = useLoaderDialog();

  const resolvedGcgrs = React.useRef<Map<string, GCGR>>(new Map());

  /** Returns the target when it turned out not to exist; undefined when it loaded. */
  /** Resolves true when the user chooses to remove the card. Takes the progress dialog's slot. */
  const confirmRemoval = (missing: MissingTarget): Promise<boolean> =>
    new Promise((resolve) => {
      showDialog({
        title: missing.notice?.title ?? DATASOURCE_NOT_FOUND_TITLE,
        description: DialogDescription(missing.notice?.description ?? datasourceGoneDescription(missing.label)),
        alert: {
          content: DATASOURCE_REMOVED_SHEETS_EMPTIED,
        },
        submit: {
          label: REMOVE_ACTION,
          action: () => {
            hideDialog();
            resolve(true);
          },
        },
        cancel: {
          label: KEEP_ACTION,
          action: () => {
            hideDialog();
            resolve(false);
          },
        },
      });
    });

  /** The same teardown each card's own remove performs, so confirming here and there agree. */
  const removeMissingDatasource = async (missing: MissingTarget): Promise<void> => {
    await clearReportWorksheets((template) => reportTypeOfTemplate(template) === missing.type);
    if (missing.type === "gcgr") {
      if (settings.hasDataSheets) await clearGcgrDataTables();
      await clearGCGRReportTable();
      clearGCGRCaches();
      setSelectedGcgr(null);
    } else if (missing.type === "contracting") {
      if (settings.hasDataSheets) await clearContractingDataTables();
      await clearContractingReportTable();
      clearContractingCaches();
      setIsContractingLoaded(false);
    } else if (missing.type === "cost-model") {
      await clearCostModelReportTable();
      clearCostModelCaches();
      setSelectedCostModel(null);
    } else {
      await clearEstimateSelection();
      if (settings.hasDataSheets) await clearEstimateDataTables();
      await clearAllChartData();
      setSelectedEstimate(null);
    }
    removeDatasource("ediphi", missing.type);
  };

  /**
   * Whether this target already has a card, which is what decides throw vs report everywhere below.
   * A pick has replaced nothing yet, so a target that cannot complete refuses outright rather than
   * offering to remove a card it never filled — removal clears by type, which on a switch would take
   * the selection the pick failed to replace. Safe to throw because add and switch are always a
   * single target. A card already on screen is refreshing beside others, so it reports or degrades
   * instead of aborting the pass and abandoning them.
   */
  const isOnACard = (target: LoadTarget): boolean => {
    switch (target.type) {
      case "estimate":
        return selectedEstimate?.id === target.estimate.id;
      case "gcgr":
        return selectedGcgr?.id === target.gcgr.id;
      case "contracting":
        return isContractingLoaded;
      case "cost-model":
        return selectedCostModel?.id === target.costModel.id;
    }
  };

  const refreshDatasource = async (target: LoadTarget, projectId: string): Promise<MissingTarget | undefined> => {
    const onACard = isOnACard(target);
    switch (target.type) {
      case "estimate": {
        const result = await refreshEstimateDatasource(projectId, target.estimate.id);
        if (!result.success || !result.data?.estimate) {
          // The export cannot tell a deleted estimate from a bad minute on the endpoint, so ask the
          // project's list. Absent means gone; still listed — or a list we could not read — means this
          // was a real failure and should surface as one.
          const listed = await fetchEstimatesByProjectId(projectId);
          if (listed.success && !(listed.data ?? []).some((estimate) => estimate.id === target.estimate.id)) {
            if (!onACard) throw new DatasourceGoneError(ESTIMATE_PICK_GONE_MESSAGE);
            return { type: "estimate", label: target.estimate.name };
          }
          throw result.success ? new Error("No estimate data returned") : result.error;
        }
        const estimateData: EstimateData = result.data;
        await tabulateEstimateReportData(estimateData);
        setSelectedEstimate(estimateData.estimate);
        return undefined;
      }
      case "gcgr": {
        const refreshResult = await refreshGCGRCaches(projectId, target.gcgr.id);
        if (!refreshResult.success) throw refreshResult.error;
        const resolved = refreshResult.data;
        // Nothing is cleared here — the pass asks before removing anything, because a missing row can
        // also be a server having a bad minute.
        if (!resolved) {
          if (!onACard) throw new DatasourceGoneError(GCGR_PICK_GONE_MESSAGE);
          return { type: "gcgr", label: target.gcgr.name };
        }
        resolvedGcgrs.current.set(target.gcgr.id, resolved);
        await tabulateGCGRReportData(projectId, resolved);
        setSelectedGcgr(resolved);
        return undefined;
      }
      case "contracting": {
        // Scope keeps its cached fallback on failure — it is non-critical in getBidTabs
        const bidGroupsResult = await refreshContractingDatasource(projectId);
        if (!bidGroupsResult.success) throw bidGroupsResult.error;
        const bidGroups = bidGroupsResult.data?.bidGroups ?? [];
        // No bid packages left is the contracting equivalent of a deleted GCGR. Previously this cleared
        // the report table and then re-asserted the selection anyway, leaving a card with nothing behind
        // it.
        if (bidGroups.length === 0) {
          if (!onACard) throw new DatasourceGoneError(CONTRACTING_PICK_GONE_MESSAGE);
          return { type: "contracting", label: "Contracting" };
        }
        await tabulateContractingReportData(projectId, bidGroups);
        setIsContractingLoaded(true);
        return undefined;
      }
      case "cost-model": {
        const { costModel: refreshResult, totals: totalsResult } = await refreshCostModelCaches(
          projectId,
          target.costModel.id
        );
        if (!refreshResult.success) throw refreshResult.error;
        const resolved = refreshResult.data;
        if (!resolved) {
          if (!onACard) throw new DatasourceGoneError(COST_MODEL_PICK_GONE_MESSAGE);
          return { type: "cost-model", label: costModelLabel(target.costModel) };
        }
        // The list this model came from needs no cost model permission, but its figures do, so they
        // are asked for here rather than letting the card discover the 403 and sit on "—" forever.
        // Access can be revoked after the fact, or the model can stop being the trendline the
        // server's surfacing fallback covered. Nothing is saved unless both answered.
        //
        // A pick refuses on any failure, as every other datasource does for its own fetch: better to
        // retry than to seat a card whose figures may never arrive. A card that already exists is
        // past that decision, so a refusal is reported — it is a standing state of the world, and
        // throwing would break section Refresh for this user every time rather than once — while
        // anything else is treated as a bad minute: the card keeps what it had, to retry later.
        //
        // An answered request with no figures in it is as unusable as a failed one, and Result makes
        // `data` optional on success, so the flag alone would let an empty payload seat the card.
        if (!totalsResult || !totalsResult.success || !totalsResult.data) {
          if (!onACard) {
            throw totalsResult && !totalsResult.success
              ? totalsResult.error
              : new Error("No cost model totals returned");
          }
          if (totalsResult && !totalsResult.success && totalsResult.error instanceof ForbiddenError) {
            return {
              type: "cost-model",
              label: costModelLabel(resolved),
              notice: {
                title: COST_MODEL_UNAVAILABLE_TITLE,
                description: `${totalsResult.error.message} ${REMOVE_ITS_CARD_PROMPT}`,
              },
            };
          }
          return undefined;
        }
        await tabulateCostModelReportData(projectId, resolved, totalsResult.data);
        setSelectedCostModel(resolved);
        return undefined;
      }
    }
  };

  const writeDataTables = async (target: LoadTarget, projectId: string): Promise<void> => {
    switch (target.type) {
      case "estimate":
        await loadEstimateData(target.estimate.id);
        return;
      case "gcgr": {
        const resolved = resolvedGcgrs.current.get(target.gcgr.id) ?? target.gcgr;
        await loadGCGRData(projectId, resolved);
        return;
      }
      case "contracting":
        await loadContractingData(projectId);
        return;
    }
  };

  /**
   * Runs one pass over `targets`: refresh their data, write their tables, then refresh the
   * workbook's reports and charts once. The phases — not the datasources — are what the progress
   * dialog steps through, so refreshing three datasources is one bar rather than three.
   */
  const loadDatasources = async (
    targets: LoadTarget[],
    options?: {
      /** Refreshing the section is one action over everything, so it never names a single datasource. */
      namePerDatasource?: boolean;
    }
  ): Promise<LoadOutcome> => {
    const namePerDatasource = options?.namePerDatasource ?? true;
    // Signed out, the sign-in screen takes over; every request would fail for want of a token.
    if (isLoading || targets.length === 0 || !hasSession()) return { dataLoaded: false, completed: false };
    isLoading = true;
    let succeeded = false;
    let dataLoaded = false;
    const missing: MissingTarget[] = [];
    let missingTypes = new Set<string>();
    let loadedTargets: LoadTarget[] = [];
    let sections: string[] = [];

    try {
      if (!selectedProject) return { dataLoaded: false, completed: false };
      const projectId = selectedProject.id;
      resolvedGcgrs.current.clear();
      clearTimeoutRef();

      const estimateTarget = targets.find((target) => target.type === "estimate");
      if (estimateTarget && estimateTarget.type === "estimate") {
        // Only on a genuine switch — the estimate row still names the previously loaded
        // estimate at this point, so a manual refresh matches and keeps the user's filters.
        if ((await getLoadedEstimateId()) !== estimateTarget.estimate.id) {
          await worksheetCache.deleteOption("filterByUseGroup");
          await worksheetCache.deleteOption("filterByAlternate");
          await worksheetCache.deleteOption("filterByType");
        }
      }

      let discoveredReports: ReportTemplate[] = [];

      await withLoading(async () => {
        try {
          await updateDialog(phaseOneTitle(targets, namePerDatasource), 0);
          await setCalculationManual();
          // Discovery runs Excel.run reads, so it finishes before the refreshes start writing
          // tables — Office.js has no concurrent Excel.run.
          const targetTypes = new Set<string>(targets.map((target) => target.type));
          discoveredReports = (await discoverReportTemplates()).filter((template) => {
            const reportType = reportTypeOfTemplate(template);
            return reportType ? targetTypes.has(reportType) : false;
          });
          const [charts] = await discoverExistingCharts();

          sections = ["datasources"];
          if (settings.hasDataSheets) sections.push("tables");
          if (discoveredReports.length > 0) sections.push("reports");
          // Charts are estimate-derived only.
          if (charts.length > 0 && estimateTarget) sections.push("charts");
          setSectionCount(sections.length);

          // Once per pass, before any datasource row: legacy __Report is rebuilt from it.
          await tabulateProjectReportData(projectId);
          for (const target of targets) {
            const miss = await refreshDatasource(target, projectId);
            if (miss) missing.push(miss);
          }
          // Only when every target's data actually landed: a target that turned out not to exist has
          // nothing to register, which is what stops the add and switch paths creating a card for it.
          dataLoaded = missing.length === 0;
          // Everything after this point works from the survivors. A target that turned out not to exist
          // has nothing to write, and for GCGR the export throws outright — which would abort the pass
          // before it ever gets to ask about removing the card.
          missingTypes = new Set(missing.map((miss) => miss.type));
          loadedTargets = targets.filter((target) => !missingTypes.has(target.type));
          // Here rather than after reports and charts: the data and selections are already written, so
          // a later-phase failure would otherwise leave card figures stale against a moved workbook.
          markDataRefreshed();
          await updateDialog(phaseOneTitle(targets, namePerDatasource), sections.indexOf("datasources") + 1);
          await new Promise((resolve) => setTimeout(resolve, 500));
        } catch (error) {
          const err: LinearLoaderError = {
            error,
            // A ForbiddenError names a permission the user can go and ask for, so it carries its own
            // wording rather than collapsing into the generic failure.
            errorMessage:
              error instanceof DatasourceGoneError || error instanceof ForbiddenError
                ? error.message
                : LOADER_DATASOURCES_ERROR,
            severity: "error",
          };
          await updateDialog(DATASOURCE_LOAD_FAILED_TITLE, sections.indexOf("datasources"), err);
          throw error;
        }

        if (sections.includes("tables")) {
          try {
            await updateDialog(LOADING_TABLES_TITLE, sections.indexOf("tables"));
            for (const target of loadedTargets) {
              await writeDataTables(target, projectId);
            }
            await updateDialog(LOADING_TABLES_TITLE, sections.indexOf("tables") + 1);
            await new Promise((resolve) => setTimeout(resolve, 500));
          } catch (error) {
            const err: LinearLoaderError = {
              error,
              errorMessage: LOADER_TABLES_ERROR,
              severity: "error",
            };
            await updateDialog(TABLES_LOAD_FAILED_TITLE, sections.indexOf("tables"), err);
            throw error;
          }
        }

        if (sections.includes("reports")) {
          try {
            await withReportLoading(async () => {
              await updateDialog(LOADING_REPORTS_TITLE, sections.indexOf("reports"));
              await refreshAllReports(
                discoveredReports.filter((template) => {
                  const reportType = reportTypeOfTemplate(template);
                  return reportType ? !missingTypes.has(reportType) : false;
                })
              );
              await updateDialog(LOADING_REPORTS_TITLE, sections.indexOf("reports") + 1);
              await new Promise((resolve) => setTimeout(resolve, 500));
            });
          } catch (error) {
            const err: LinearLoaderError = {
              error,
              errorMessage: LOADER_REPORTS_ERROR,
              severity: "error",
            };
            await updateDialog(REPORTS_LOAD_FAILED_TITLE, sections.indexOf("reports"), err);
            throw error;
          }
        }

        if (sections.includes("charts")) {
          try {
            await withChartLoading(async () => {
              await updateDialog(LOADING_CHARTS_TITLE, sections.indexOf("charts"));
              if (!missingTypes.has("estimate")) await refreshAllCharts(settings);
              await updateDialog(LOADING_CHARTS_TITLE, sections.indexOf("charts") + 1);
              await new Promise((resolve) => setTimeout(resolve, 500));
            });
          } catch (error) {
            const err: LinearLoaderError = {
              error,
              errorMessage: LOADER_CHARTS_ERROR,
              severity: "error",
            };
            await updateDialog(CHARTS_LOAD_FAILED_TITLE, sections.indexOf("charts"), err);
            throw error;
          }
        }
      });

      // Asked after the pass, not when discovered: updateDialog treats any message as terminal, so a
      // mid-pass notice would either halt the load or be overwritten by the next phase. One dialog per
      // card — the decision is per card, and more than one missing at once is rare.
      for (const miss of missing) {
        if (await confirmRemoval(miss)) await removeMissingDatasource(miss);
      }
      if (missing.length === 0) showSuccessAndAutoClose();
      succeeded = true;
    } catch (error) {
      const err = handleError(error, { context: "loadDatasources" });
      // A session that ended mid-pass fails every request after it; the sign-in screen covers that.
      if (hasSession()) Logger.error("Error loading datasource data", err);
    } finally {
      reset();
      isLoading = false;
      await setCalculationAutomatic();
    }

    return { dataLoaded, completed: succeeded };
  };

  return { loadDatasources };
}

/** The load targets for the given loaded datasources, in card order; every loaded one by default. */
export function useLoadTargets() {
  const { ediphiDatasources } = useDatasources();
  const { selectedEstimate, selectedGcgr, isContractingLoaded, selectedCostModel } = useProject();

  return (types: EdiphiDatasourceType[] = ediphiDatasources): LoadTarget[] =>
    ediphiDatasources.reduce<LoadTarget[]>((acc, type) => {
      if (!types.includes(type)) return acc;
      if (type === "estimate" && selectedEstimate) acc.push({ type: "estimate", estimate: selectedEstimate });
      if (type === "gcgr" && selectedGcgr) acc.push({ type: "gcgr", gcgr: selectedGcgr });
      if (type === "contracting" && isContractingLoaded) acc.push({ type: "contracting" });
      if (type === "cost-model" && selectedCostModel) acc.push({ type: "cost-model", costModel: selectedCostModel });
      return acc;
    }, []);
}
